import type { FormEvent, ReactNode } from "react";
import { Alert, Button, Modal } from "antd";
import { useI18n } from "../i18n";

/**
 * Shared modal for form-based actions across portal pages.
 *
 * Wraps antd Modal with the portal's conventions: a submit/cancel footer
 * driven by the form's own submit event, inline error surfacing, and busy
 * state on the primary action. Pages supply only the fields.
 */
export function FormModal({
  open,
  title,
  description,
  children,
  onCancel,
  onSubmit,
  submitLabel,
  cancelLabel,
  busy = false,
  disabled = false,
  danger = false,
  error,
  width = 460,
}: {
  open: boolean;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  onCancel: () => void;
  onSubmit: () => void;
  submitLabel?: ReactNode;
  cancelLabel?: ReactNode;
  busy?: boolean;
  disabled?: boolean;
  danger?: boolean;
  /** Surfaced as an Alert above the footer when a mutation fails. */
  error?: Error | null;
  width?: number;
}) {
  const { locale } = useI18n();
  const isChinese = locale === "zh-CN";

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!busy && !disabled) onSubmit();
  }

  return (
    <Modal
      className="portal-form-modal"
      rootClassName="portal-form-modal-root"
      open={open}
      title={title}
      onCancel={onCancel}
      footer={null}
      width={width}
      destroyOnHidden
      centered
      maskClosable={!busy}
      keyboard={!busy}
    >
      <form className="portal-form-modal__form" onSubmit={handleSubmit}>
        {description ? (
          <p className="portal-form-modal__description">{description}</p>
        ) : null}

        <div className="portal-form-modal__fields">{children}</div>

        {error ? (
          <Alert
            type="error"
            showIcon
            className="portal-form-modal__error"
            message={isChinese ? "操作未完成" : "Action not completed"}
            description={error.message}
          />
        ) : null}

        <div className="portal-form-modal__actions">
          <Button onClick={onCancel} disabled={busy}>
            {cancelLabel ?? (isChinese ? "取消" : "Cancel")}
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            danger={danger}
            loading={busy}
            disabled={disabled}
          >
            {submitLabel ?? (isChinese ? "确认" : "Confirm")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
